import { Button, FlexBox, getTokenCssVar, Icon, IconButton, useScreenClass } from '@vp/swan'
import { useEffect, useMemo, useState } from 'react'
import { getLocalStorageWithExpiry, setLocalStorageWithExpiry } from '../../../utils/local-storage'
import { getCookie, setCookie } from '../../../utils/cookies'
import { languageBarEnabledCountries } from '../../../constants/language-bar'
import { COOKIE_KEYS, LOCAL_STORAGE_KEYS, STORAGE_TTL } from '../../../constants/storage-keys'
import { getHeaderTracker } from '../../../tracking'
import { getRedirectURL } from '../../../utils/page-availability'
import { useTenantConfig } from '../../../context/TenantConfigContext'
import { deriveLanguageBarData } from '../../../utils/tenant-language-bar-data'
import { useI18n } from '../../../context/TranslationContext'

const tracker = {
  trackView(language: string) {
    getHeaderTracker().trackFlyOutView('Language Bar', `view_${language}`)
  },
  trackSelect(language: string) {
    getHeaderTracker().trackFlyOutClick('Language Bar', `select_${language}`)
  },
  trackClose(language: string) {
    getHeaderTracker().trackFlyOutClick('Language Bar', `close_${language}`)
  },
}

export default function LanguageBar({ locale }: Readonly<{ locale: string }>) {
  const screen = useScreenClass()
  const isMobile = screen === 'xs'

  const tenantConfig = useTenantConfig()

  const [websiteLanguage, webSiteCountry] = locale.split('-') as [string | undefined, string | undefined]

  // Use tenant-config-derived data when available, fall back to hardcoded VistaPrint data
  const availableLanguages: Array<{ url: string; text: string; title: string; localeCode: string }> | undefined = useMemo(() => {
    const country = webSiteCountry?.toUpperCase()
    if (!country) return undefined
    if (tenantConfig) {
      const derivedData = deriveLanguageBarData(tenantConfig)
      return derivedData[country]?.alternateLanguages
    }
    return languageBarEnabledCountries[country as keyof typeof languageBarEnabledCountries]?.alternateLanguages
  }, [tenantConfig, webSiteCountry])

  const { showLanguageBar, hideLanguageBar } = useLanguageBarVisibility(websiteLanguage, webSiteCountry, !!availableLanguages?.length)

  const handleClose = () => {
    tracker.trackClose(websiteLanguage ?? '')
    hideLanguageBar()
    setLocalStorageWithExpiry(LOCAL_STORAGE_KEYS.HIDE_LANGUAGE_BAR, 'true', STORAGE_TTL.ONE_WEEK)
  }

  const handleLanguageSelection = async (url: string, localeCode: string, languageText: string) => {
    tracker.trackSelect(languageText)
    setCookie(COOKIE_KEYS.HIDE_LANGUAGE_BAR_SESSION, 'true')
    const userLanguageChoice = localeCode.split('-')[0]

    if (websiteLanguage === userLanguageChoice) {
      hideLanguageBar()
    } else {
      const currentPath = globalThis.location?.pathname ?? '/'
      const redirectPath = await getRedirectURL({ currentUrl: currentPath, requestLocale: localeCode, locale })
      const redirectUrl = redirectPath === '/' ? url : `${globalThis.location.origin}${redirectPath}`
      globalThis.location.href = redirectUrl
    }
  }

  if (!showLanguageBar || !availableLanguages?.length) return null

  return (
    <FlexBox
      justifyContent="center"
      fontSkin="body-small"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      textColor="standard"
      darkMode
      gap={4}
      p={3}
      flexDirection={{ xs: 'column', sm: 'row' }}
      style={{ backgroundColor: getTokenCssVar('SwanSemColorBgStrong'), position: 'relative' }}
    >
      <FlexBox m={0} fontSkin="body-small-bold" flexWrap="wrap" style={{ whiteSpace: 'pre-line' }}>
        {availableLanguages.map(({ title }) => title).join('\n')}
      </FlexBox>

      <FlexBox justifyContent="space-between" gap={{ xs: '3', sm: '4' }} alignItems="center" style={{ width: isMobile ? '100%' : 'auto' }}>
        {availableLanguages.map(({ url, text, localeCode }) => (
          <Button
            key={text}
            skin="secondary"
            py={3}
            px={6}
            width={isMobile ? 'full-width' : 'standard'}
            textColor="standard"
            fontSkin="body-small"
            onClick={() => handleLanguageSelection(url, localeCode, text)}
          >
            {text}
          </Button>
        ))}
      </FlexBox>
      <CloseButton isMobile={isMobile} onClick={handleClose} />
    </FlexBox>
  )
}

const useLanguageBarVisibility = (websiteLanguage?: string, webSiteCountry?: string, hasAvailableLanguages?: boolean) => {
  const [showLanguageBar, setShowLanguageBar] = useState(false)
  const hideLanguageBar = () => setShowLanguageBar(false)

  useEffect(() => {
    const languageBarHidden = getLocalStorageWithExpiry(LOCAL_STORAGE_KEYS.HIDE_LANGUAGE_BAR) || getCookie(COOKIE_KEYS.HIDE_LANGUAGE_BAR_SESSION)
    const userLocale = navigator.languages.length ? navigator.languages[0] : navigator.language

    const userPreferredLanguage = userLocale?.split('-')[0]

    if (websiteLanguage !== userPreferredLanguage && hasAvailableLanguages && languageBarHidden !== 'true') {
      setShowLanguageBar(true)
      tracker.trackView(websiteLanguage ?? '')
    }
  }, [websiteLanguage, webSiteCountry, hasAvailableLanguages])

  return { showLanguageBar, hideLanguageBar }
}

interface CloseButtonProps {
  isMobile: boolean
  onClick: () => void
}

function CloseButton({ isMobile, onClick }: Readonly<CloseButtonProps>) {
  const { translate } = useI18n()
  const mobileStyles = { position: 'absolute' as const, top: 0, right: 0 }

  return (
    <IconButton
      tooltip={translate('close')}
      skin="tertiary"
      p={3}
      textColor="standard"
      style={{ height: getTokenCssVar('SwanSemSpace7'), ...(isMobile ? mobileStyles : {}) }}
      onClick={onClick}
    >
      <Icon iconType="close" size="16p" />
    </IconButton>
  )
}
